import React, { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import { AccountTransactionRecord, TransactionDetailRecord } from '../services/transaction/types';
import { TransactionEntity } from './types';
import { useTransactionService } from '../services/transaction';

type TransactionGridContextInstance = {
    transactions: AccountTransactionRecord[] | null;
    spotlighted: TransactionDetailRecord | null;
    openSidePanel: (transactionId: string, source: TransactionEntity) => void;
    closeSidePanel: () => void;
}

const GridContext : React.Context<TransactionGridContextInstance> = React.createContext<TransactionGridContextInstance>({
    transactions: [],
    spotlighted: null,
    openSidePanel: () => {},
    closeSidePanel: () => {},
});

export const TransactionGridContextProvider = ({ children }: { children: React.ReactNode }) => {
    const service = useTransactionService()
    const [ transactions, setTransactions ] = useState<AccountTransactionRecord[] | null>(null);
    const [ spotlighted, setSpotlighted ] = useState<TransactionDetailRecord | null>(null);

    useEffect(() => {
        service.getTransactions().then(response => {
            if (!('status' in response)) {
                setTransactions(response);
            }
        });
    }, [service]);

    const openSidePanel = useCallback((transactionId: string, source: TransactionEntity) => {
        switch (source){
            case TransactionEntity.IMPACT:
                service.getRewardableTransaction(transactionId).then(response => {
                    if ( ('id' in response && 'amount' in response) ) {
                        setSpotlighted(response);
                    }
                });
                break;
            default:
                console.error('Unknown source: ' + source);
                break;
        }
    }, [service]);

    const closeSidePanel = useCallback(() => {
        setSpotlighted(null);
    }, []);

    const contextValue = useMemo(() => ({
        transactions,
        spotlighted,
        openSidePanel,
        closeSidePanel
    }), [transactions, spotlighted, openSidePanel, closeSidePanel]);

    return (
        <GridContext.Provider value={contextValue}>
            {children}
        </GridContext.Provider>
    )
}

export const useTransactionGridContext = () => useContext<TransactionGridContextInstance>(GridContext);